import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './fonts/gotham/gotham.css';
import './LandingPage.css';
import { supabase } from './supabaseClient';
import LandingShopsMap from './LandingShopsMap';

import logo from './assets/mmjlogo.png';
import phoneMockup from './assets/phone.png';

const LP_NAV_SECTIONS = [
  { id: 'stats', label: 'Fırsatlar' },
  { id: 'how', label: 'Nasıl çalışır' },
  { id: 'takvim', label: 'Kampanyalar' },
  { id: 'contact', label: 'İletişim' },
];

const LP_NEAR_JOI_URL =
  (process.env.REACT_APP_NEAR_JOI_URL || '').trim() || 'https://makemejoi.com';

/** Sosyal bağlantılar — `REACT_APP_SOCIAL_*` ile geçersiz kılınır. */
const LP_FOOTER_SOCIAL = [
  {
    id: 'instagram',
    label: 'Instagram',
    href:
      (process.env.REACT_APP_SOCIAL_INSTAGRAM_URL || '').trim() ||
      'https://www.instagram.com/makeme.joi/',
  },
  {
    id: 'facebook',
    label: 'Facebook',
    href:
      (process.env.REACT_APP_SOCIAL_FACEBOOK_URL || '').trim() ||
      'https://www.facebook.com/makemejoi',
  },
  {
    id: 'x',
    label: 'X',
    href: (process.env.REACT_APP_SOCIAL_X_URL || '').trim() || 'https://x.com/makemejoi',
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    href:
      (process.env.REACT_APP_SOCIAL_TIKTOK_URL || '').trim() ||
      'https://www.tiktok.com/@makemejoi',
  },
];

function LpFooterSocialSvg({ id }) {
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.65, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (id) {
    case 'instagram':
      return (
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" {...stroke} />
          <circle cx="12" cy="12" r="4" {...stroke} />
          <circle cx="17.5" cy="6.5" r="1.25" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'facebook':
      return (
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
          <path
            d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"
            {...stroke}
          />
        </svg>
      );
    case 'x':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
          <path d="M4 4l16 16M20 4L4 20" {...stroke} strokeWidth="1.85" />
        </svg>
      );
    case 'tiktok':
      return (
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
          <path
            d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"
            {...stroke}
          />
        </svg>
      );
    default:
      return null;
  }
}

function lpSectionDocumentTop(el) {
  const rect = el.getBoundingClientRect();
  return rect.top + window.scrollY;
}

function lpPickActiveNavSectionId() {
  const headerBand = 88;
  const y = window.scrollY + headerBand;
  const firstId = LP_NAV_SECTIONS[0]?.id;
  const firstEl = firstId ? document.getElementById(firstId) : null;
  if (!firstEl) return null;
  if (y < lpSectionDocumentTop(firstEl)) return null;

  let current = firstId;
  for (const { id } of LP_NAV_SECTIONS) {
    const el = document.getElementById(id);
    if (!el) continue;
    if (lpSectionDocumentTop(el) <= y) current = id;
  }
  return current;
}

function lpScrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return true;
}

export default function LandingPage() {
  const [solid, setSolid] = useState(false);
  const [activeNavSectionId, setActiveNavSectionId] = useState(null);
  const [navRule, setNavRule] = useState({ left: 0, width: 0, opacity: 0 });
  const headerNavRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  /** `null` = loading; array when fetch settled */
  const [promotions, setPromotions] = useState(null);
  /** `null` = loading; array when fetch settled */
  const [landingShops, setLandingShops] = useState(null);
  const year = new Date().getFullYear();
  const promosLoading = promotions === null;
  const shopsLoading = landingShops === null;
  const navSections =
    promosLoading || (promotions && promotions.length > 0)
      ? LP_NAV_SECTIONS
      : LP_NAV_SECTIONS.filter((s) => s.id !== 'takvim');

  useEffect(() => {
    const fn = () => setSolid(window.scrollY > 48);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    const nodes = document.querySelectorAll('.lp-reveal');
    if (!nodes.length) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 },
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    let ticking = false;
    const update = () => {
      setActiveNavSectionId(lpPickActiveNavSectionId());
      ticking = false;
    };
    const onScrollOrResize = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, []);

  useLayoutEffect(() => {
    const nav = headerNavRef.current;
    if (!nav) return undefined;

    const updateRule = () => {
      if (!activeNavSectionId) {
        setNavRule((prev) => ({ ...prev, opacity: 0 }));
        return;
      }
      const link = nav.querySelector(`a[href="#${activeNavSectionId}"]`);
      if (!link) {
        setNavRule((prev) => ({ ...prev, opacity: 0 }));
        return;
      }
      const navRect = nav.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      setNavRule({
        left: linkRect.left - navRect.left,
        width: linkRect.width,
        opacity: 1,
      });
    };

    updateRule();
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(updateRule);
    });
    ro.observe(nav);
    window.addEventListener('resize', updateRule, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateRule);
    };
  }, [activeNavSectionId]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const onNavSectionClick = (event, id) => {
    event.preventDefault();
    setMenuOpen(false);
    // Wait a frame so the mobile drawer unlocks body scroll first.
    requestAnimationFrame(() => {
      lpScrollToSection(id);
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('shops')
        .select('id, shop, description, mapslink, location')
        .order('id', { ascending: true });
      if (cancelled) return;
      if (!error && Array.isArray(data)) setLandingShops(data);
      else setLandingShops([]);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('promotions')
        .select('id, title, description, image_url, url')
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (!error && Array.isArray(data)) setPromotions(data);
      else setPromotions([]);
    })();
    return () => { cancelled = true; };
  }, []);

  // Deep-link: /#takvim etc. after sections mount / finish loading.
  useEffect(() => {
    const hash = (window.location.hash || '').replace(/^#/, '');
    if (!hash) return undefined;
    const t = window.setTimeout(() => {
      lpScrollToSection(hash);
    }, 80);
    return () => window.clearTimeout(t);
  }, [promotions, landingShops]);

  return (
    <div className="lp">

      {/* NAV */}
      <header className={`lp-header${solid || menuOpen ? ' solid' : ''}`}>
        <div className="lp-header-brand">
          <img src={logo} alt="Make Me JOI" className="lp-logo" />
        </div>
        <nav ref={headerNavRef} className="lp-header-nav" aria-label="Ana menü">
          {navSections.map(({ id, label }) => (
            <a
              key={id}
              href={`#${id}`}
              className={activeNavSectionId === id ? 'is-active' : undefined}
              aria-current={activeNavSectionId === id ? 'location' : undefined}
              onClick={(e) => onNavSectionClick(e, id)}
            >
              {label}
            </a>
          ))}
          <span
            className="lp-header-nav-rule"
            aria-hidden
            style={{
              left: navRule.left,
              width: navRule.width,
              opacity: navRule.opacity,
            }}
          />
        </nav>
        <div className="lp-header-actions">
          <Link to="/indir" className="lp-header-cta">
            Uygulamayı indir
          </Link>
        </div>
        <button
          type="button"
          className={`lp-burger${menuOpen ? ' open' : ''}`}
          aria-expanded={menuOpen}
          aria-controls="lp-mobile-menu"
          aria-label={menuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span />
          <span />
          <span />
        </button>
      </header>

      <div
        className={`lp-nav-backdrop${menuOpen ? ' open' : ''}`}
        aria-hidden
        onClick={() => setMenuOpen(false)}
      />

      <nav
        id="lp-mobile-menu"
        className={`lp-nav${menuOpen ? ' open' : ''}`}
        aria-label="Mobil menü"
        aria-hidden={!menuOpen}
      >
        <div className="lp-nav-sheet">
          <p className="lp-nav-kicker">Menü</p>
          <div className="lp-nav-links">
            {navSections.map(({ id, label }, index) => {
              const active = activeNavSectionId === id;
              return (
                <a
                  key={id}
                  href={`#${id}`}
                  className={active ? 'is-active' : undefined}
                  aria-current={active ? 'location' : undefined}
                  style={{ '--lp-nav-i': index }}
                  onClick={(e) => onNavSectionClick(e, id)}
                >
                  <span className="lp-nav-rail" aria-hidden>
                    <span className="lp-nav-rail-dot" />
                  </span>
                  <span className="lp-nav-idx" aria-hidden>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="lp-nav-label">{label}</span>
                  {active ? (
                    <span className="lp-nav-now">Şu an</span>
                  ) : null}
                </a>
              );
            })}
          </div>
          <div className="lp-nav-actions">
            <Link
              to="/indir"
              className="lp-nav-cta"
              onClick={() => setMenuOpen(false)}
            >
              Uygulamayı indir
            </Link>
            <Link
              to="/login"
              className="lp-nav-secondary"
              onClick={() => setMenuOpen(false)}
            >
              Giriş Yap
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="lp-hero" id="stats">
        <div className="lp-hero-top">
          <div className="lp-hero-copy">
            <h1 className="lp-hero-tagline">
              <span className="lp-hero-tagline-line">
                <span className="lp-hero-tagline-light">Eat</span>
                <span className="lp-hero-tagline-well">Well.</span>
              </span>
              <span className="lp-hero-tagline-line">
                <span className="lp-hero-tagline-light">Feel</span>
                <span className="lp-hero-tagline-well">Well.</span>
              </span>
            </h1>
            <p className="lp-hero-lead">
              Her alışverişte <span className="lp-hero-accent">%10</span> tasarruf.
            </p>
            <p className="lp-hero-sub">
              6 içecek alın, 1 bedava kazanın.
            </p>
            <div className="lp-hero-btns">
              <Link to="/indir" className="lp-cta">Uygulamayı indir</Link>
            </div>
          </div>
        </div>
        <div className="lp-hero-green" id="how">
          <div className="lp-hero-green-face" aria-hidden />
          <div className="lp-steps-inner lp-reveal">
            <p className="lp-section-label">3 adımda</p>
            <h2 className="lp-section-h lp-section-h-tight">Nasıl çalışır?</h2>
            <ol className="lp-steps-list">
              <li className="lp-step">
                <span className="lp-step-num" aria-hidden>1</span>
                <div className="lp-step-body">
                  <h3>Kayıt ol</h3>
                  <p className="lp-step-lead">
                    Uygulamayı indirip hesabını oluşturarak sadakat kartına başlayın; tüm avantajlar tek yerde.
                  </p>
                </div>
              </li>
              <li className="lp-step">
                <span className="lp-step-num" aria-hidden>2</span>
                <div className="lp-step-body">
                  <h3>Damgaları topla</h3>
                  <p className="lp-step-lead">
                    Her uygun içecek alışverişinde kartınıza damga eklenir; hedefe ulaşınca ödülünüzü kullanın.
                  </p>
                </div>
              </li>
              <li className="lp-step">
                <span className="lp-step-num" aria-hidden>3</span>
                <div className="lp-step-body">
                  <h3>Her siparişinde %10 indirimden faydalan</h3>
                  <p className="lp-step-lead">
                    Programa dahil müşteriler, geçerli ürün ve şubelerde sipariş başına indirimden yararlanır.
                  </p>
                </div>
              </li>
            </ol>
          </div>
          <div className="lp-hero-phone">
            <img
              src={phoneMockup}
              alt="Make Me JOI uygulaması"
              className="lp-hero-phone-img"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {promosLoading || (promotions && promotions.length > 0) ? (
      <section
        className="lp-app-calendar lp-reveal"
        id="takvim"
        aria-busy={promosLoading || undefined}
      >
        <div className="lp-app-calendar-inner">
            <div className="lp-landing-promos" aria-label="Kampanyalar ve duyurular">
              <p className="lp-section-label">Kampanyalar</p>
              <h2 className="lp-section-h lp-app-calendar-title">Kampanyalar</h2>
              {promosLoading ? (
                <div
                  className="lp-landing-promos-grid"
                  aria-label="Kampanyalar yükleniyor"
                >
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="lp-landing-promo-card lp-skel-card" aria-hidden>
                      <div className="lp-landing-promo-media lp-skel" />
                      <div className="lp-landing-promo-text">
                        <span className="lp-skel lp-skel-line lp-skel-line--title" />
                        <span className="lp-skel lp-skel-line" />
                        <span className="lp-skel lp-skel-line lp-skel-line--short" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <div className="lp-landing-promos-grid">
                {promotions.map((ad) => {
                  const body = (
                    <>
                      {ad.image_url ? (
                        <div className="lp-landing-promo-media">
                          <img
                            src={ad.image_url}
                            alt={ad.title ? String(ad.title) : ''}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      ) : null}
                      <div className="lp-landing-promo-text">
                        {ad.title ? (
                          <h3 className="lp-landing-promo-title">{ad.title}</h3>
                        ) : null}
                        {ad.description ? (
                          <p className="lp-landing-promo-desc">{ad.description}</p>
                        ) : null}
                      </div>
                    </>
                  );
                  if (ad.url && String(ad.url).trim()) {
                    return (
                      <a
                        key={ad.id}
                        className="lp-landing-promo-card lp-landing-promo-card--link"
                        href={String(ad.url).trim()}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {body}
                      </a>
                    );
                  }
                  return (
                    <article key={ad.id} className="lp-landing-promo-card">
                      {body}
                    </article>
                  );
                })}
              </div>
              )}
            </div>
        </div>
      </section>
      ) : null}

      <section
        className="lp-near-joi lp-near-joi--tail lp-reveal"
        id="stores"
        aria-busy={shopsLoading || undefined}
      >
        <div className="lp-near-joi-locations">
          {shopsLoading ? (
            <div className="lp-near-joi-shops" aria-label="Şubeler yükleniyor">
              <p className="lp-near-joi-loc-label" lang="tr">
                Şubeler
              </p>
              <div className="lp-near-joi-intro lp-near-joi-intro--under-loc" lang="en">
                <h2 className="lp-near-joi-headline">
                  Aradığın lezzetleri buldun!
                </h2>
                <p className="lp-near-joi-line">Sana en yakın şubelerimiz.</p>
              </div>
              <div className="lp-near-joi-split">
                <div className="lp-near-joi-split-list">
                  <ul className="lp-loc-list lp-loc-list--landing" aria-hidden>
                    {[0, 1, 2, 3].map((i) => (
                      <li key={i}>
                        <div className="lp-loc-row lp-skel-loc-row">
                          <span className="lp-skel lp-skel-idx" />
                          <div className="lp-skel-loc-copy">
                            <span className="lp-skel lp-skel-line lp-skel-line--title" />
                            <span className="lp-skel lp-skel-line lp-skel-line--short" />
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lp-near-joi-split-map">
                  <div className="lp-shops-map-wrap lp-skel-map" aria-hidden>
                    <div className="lp-skel lp-skel-map-fill" />
                  </div>
                </div>
              </div>
            </div>
          ) : landingShops.length > 0 ? (
            <div className="lp-near-joi-shops">
              <p className="lp-near-joi-loc-label" lang="tr">
                Şubeler
              </p>
              <div className="lp-near-joi-intro lp-near-joi-intro--under-loc" lang="en">
                <h2 className="lp-near-joi-headline">
                  Aradığın lezzetleri buldun!
                </h2>
                <p className="lp-near-joi-line">Sana en yakın şubelerimiz.</p>
      
              </div>
              <div className="lp-near-joi-split">
                <div className="lp-near-joi-split-list">
                  <ul className="lp-loc-list lp-loc-list--landing">
                    {landingShops.map((s, i) => {
                      const name = (s.shop || '').trim() || 'Şube';
                      const desc = (s.description || '').trim();
                      const maps = (s.mapslink || '').trim();
                      const idx = String(i + 1).padStart(2, '0');
                      const body = (
                        <>
                          <span className="lp-loc-idx" aria-hidden>
                            {idx}
                          </span>
                          <div>
                            <strong>{name}</strong>
                            {desc ? <small>{desc}</small> : null}
                          </div>
                          {maps ? (
                            <span className="lp-loc-arrow" aria-hidden>
                              →
                            </span>
                          ) : null}
                        </>
                      );
                      return (
                        <li key={s.id}>
                          {maps ? (
                            <a
                              href={maps}
                              className="lp-loc-row lp-loc-row--link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {body}
                            </a>
                          ) : (
                            <div className="lp-loc-row">{body}</div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="lp-near-joi-split-map">
                  <LandingShopsMap rawRows={landingShops} />
                </div>
              </div>
            </div>
          ) : (
            <div className="lp-near-joi-intro" lang="en">
              <h2 className="lp-near-joi-headline">
                Need a vitamin boost or a Cup of Joi?
              </h2>
              <p className="lp-near-joi-line">We might just be around the corner.</p>
              <a
                href={LP_NEAR_JOI_URL}
                className="lp-near-joi-cta"
                target="_blank"
                rel="noopener noreferrer"
              >
                Find your nearest Joi!
              </a>
            </div>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer" id="contact">
        <div className="lp-footer-inner">
          <div className="lp-footer-main">
            <div className="lp-footer-brand">
              <p className="lp-footer-legal-name">
                MAKE ME JOİ GIDA TİCARET ANONİM ŞİRKETİ
              </p>
              <p className="lp-footer-address">
                YENİBOSNA MERKEZ MAH. CEMAL ULUSOY CAD. AVVA GIYIM NO: 49
                BAHÇELİEVLER / İSTANBUL
              </p>
              <p className="lp-footer-tag">
                Her alışverişte %10 tasarruf, tek uygulamada.
              </p>
            </div>
            <div className="lp-footer-col">
              <h4 className="lp-footer-heading">Hızlı bağlantılar</h4>
              <nav className="lp-footer-nav">
                <Link to="/indir" className="lp-footer-link">İndir</Link>
                <Link to="/login" className="lp-footer-link">Giriş Yap</Link>
                <a
                  href="#stores"
                  className="lp-footer-link"
                  onClick={(e) => onNavSectionClick(e, 'stores')}
                >
                  Şubeler
                </a>
              </nav>
            </div>
          </div>
          <nav className="lp-footer-social" aria-label="Sosyal medya">
            {LP_FOOTER_SOCIAL.map((s) => (
              <a
                key={s.id}
                className="lp-footer-social-link"
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
              >
                <LpFooterSocialSvg id={s.id} />
              </a>
            ))}
          </nav>
          <div className="lp-footer-divider" />
          <div className="lp-footer-bottom">
            <p className="lp-footer-meta" lang="tr">
              <span className="lp-footer-campaign">
                MAKE ME JOI KAMPANYA HAKKINDAKİ TÜM HAKLARINI GİZLİ TUTAR.
              </span>
              <span className="lp-footer-meta-sep" aria-hidden>
                ·
              </span>
              <span className="lp-footer-copy">© {year}</span>
            </p>
            <p className="lp-footer-powered">
              powered by{' '}
              <a
                href="https://caffein.club"
                className="lp-footer-powered-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                caffein.club
              </a>
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
}
